import React, { FC, useEffect, useRef, useState } from 'react';
import { Image } from '@design-system/image';

import { RingWithProductProps } from '../models';

const RingWithProduct: FC<RingWithProductProps> = ({
  cx,
  cy,
  r,
  mainImage,
  mainImageAlt,
  floatAmp = 6,
  floatSpeed = 0.4,
  bubble,
}) => {
  const bubbleRef = useRef<HTMLImageElement>(null);
  const productRef = useRef<HTMLImageElement>(null);
  const start = useRef<number | null>(null);
  const raf = useRef<number | null>(null);

  const [productDims, setProductDims] = useState({
    width: 0,
    height: 0,
  });

  // ---------- LOAD PRODUCT SIZE ----------
  useEffect(() => {
    if (!productRef.current) return;

    const img = productRef.current;
    const naturalW = img.naturalWidth || 1;
    const naturalH = img.naturalHeight || 1;

    const productW = r * 0.68 * 2;
    const productH = (productW * naturalH) / naturalW;

    setProductDims({ width: productW, height: productH });
  }, [r, mainImage]);

  // ---------- FLOAT ANIMATION ----------
  useEffect(() => {
    const animate = (t: number) => {
      if (start.current == null) start.current = t;
      const elapsed = (t - start.current) / 1000;
      const phase = elapsed * floatSpeed * 2 * Math.PI;

      const offsetX = Math.sin(phase) * floatAmp * 0.6;
      const offsetY = Math.cos(phase * 0.8) * floatAmp;

      const ampMod = 0.6 + Math.sin(elapsed * 0.2) * 0.4;

      if (bubbleRef.current && productRef.current) {
        bubbleRef.current.style.transform = `translate(${offsetX * ampMod}px, ${
          offsetY * ampMod
        }px)`;
        productRef.current.style.transform = `
          translate(${offsetX * 0.7 * ampMod}px, ${offsetY * 0.7 * ampMod}px)
          rotate(${Math.sin(phase * 0.4) * 4}deg)
        `;
      }

      raf.current = requestAnimationFrame(animate);
    };

    raf.current = requestAnimationFrame(animate);

    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [floatAmp, floatSpeed]);

  // ---------- FINAL COORDINATES ----------
  if (cx == null || cy == null || r == null || !mainImage || !bubble) {
    return null;
  }
  const bubbleLeft = cx - r;
  const bubbleTop = cy - r;

  const productLeft = cx - productDims.width / 2;
  const productTop = cy - productDims.height / 2;

  return (
    <>
      <Image
        ref={bubbleRef}
        src={bubble}
        role="presentation"
        style={{
          position: 'absolute',
          left: bubbleLeft,
          top: bubbleTop,
          width: r * 2,
          height: r * 2,
          pointerEvents: 'none',
          zIndex: 3,
          willChange: 'transform',
        }}
      />

      <Image
        ref={productRef}
        src={mainImage}
        alt={mainImageAlt}
        objectFit="contain"
        style={{
          position: 'absolute',
          left: productLeft,
          top: productTop,
          width: productDims.width,
          height: productDims.height,
          filter: 'drop-shadow(0 10px 18px rgba(0,0,0,.35))',
          pointerEvents: 'none',
          zIndex: 4,
          willChange: 'transform',
        }}
      />
    </>
  );
};

export default RingWithProduct;
